// Drawing half of the framed operating area (the geometry half lives in
// ops/view.ts, which stays pure). Two marks, deliberately separate:
//
//   the FRAME — the confirmed operating area. A thin accent rectangle that says
//   "this block is what's loaded"; it is chrome, not a measurement, so it never
//   competes with the field underneath it.
//
//   the GRID — the model's TRUE cell boundaries inside that frame. The reach
//   field is a bilinear-smoothed canvas, which flatters the data: at the 250 nm
//   rung a 0.25 deg field is only ~17 cells across, and a smooth ramp hides that.
//   Drawing the real graticule is the cheapest honest way to show a bridge officer
//   how coarse the thing they're reading actually is. This is why the grid snaps to
//   absolute multiples of the cell size rather than to the frame's edges: a
//   graticule aligned to the bbox would be a decoration, not the model.
//
// Both are idempotent — the console re-asserts them on every theater change — and
// both survive being called while MapLibre is swapping styles (see `whenReady`).
import type { Map as MlMap } from "maplibre-gl";
import type { GeoJSONSource } from "maplibre-gl";
// MapLibre's paint DSL is a large tuple union that array literals don't match
// structurally; this is MapLibre's own type, used only to assert the literals below.
import type { ExpressionSpecification } from "@maplibre/maplibre-gl-style-spec";
import type { Bounds } from "../ops/view.js";

const FRAME_SRC = "theater-frame";
const FRAME_LINE = "theater-frame-line";
const GRID_SRC = "theater-grid";
const GRID_LINE = "theater-grid-line";

/** GFS-Wave's native resolution. The default exists so callers don't have to
 *  repeat the number; a caller on a different field must pass its own. */
const DEFAULT_CELL_DEG = 0.25;

/** Ceiling on drawn graticule lines per axis.
 *
 *  A basin-rung theater at 0.25 deg would be ~80 lines a side, and a caller
 *  passing a finer cell (or a bad bbox) could ask for thousands — enough to turn
 *  the map into a grey wash and to stall the tessellator. When the cap bites we
 *  coarsen by a WHOLE multiple of the cell so every drawn line is still a real
 *  cell boundary; showing every 2nd or 4th edge is honest, showing evenly-spaced
 *  lines that aren't edges is not. */
const MAX_LINES_PER_AXIS = 90;

// ---------- palette ----------
// Read at draw time so the map follows the Day/Dusk/Night switch, with hard
// fallbacks for the window before style.css has applied (same contract as
// overlays.ts, which owns the equivalent table for its own layers).
const CSS_FALLBACK: Record<string, string> = {
  "--accent": "#0f6b74",
  "--ink-2": "#48575f",
  "--muted": "#8a969b",
};
function cssVar(name: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || CSS_FALLBACK[name] || "#000000";
}

// The frame is chrome: accent hue for "this is the app talking", but hairline and
// half-transparent so it reads as a boundary rather than as another data layer.
const FRAME_WIDTH = 1.3;
const FRAME_OPACITY = 0.55;
// The graticule is quieter still — ink at low alpha, sub-pixel width. Note we do
// NOT use --line-2 here even though it is the app's rule colour: it ships with
// alpha already baked in, so pairing it with a line-opacity would compound two
// transparencies and the result would drift between themes.
const GRID_WIDTH = 0.5;
const GRID_OPACITY = 0.28;

// ---------- layer ordering ----------

/** The layer these marks must sit UNDER.
 *
 *  Same problem beforeBuoys() solves in overlays.ts, but we can't reuse it: the
 *  buoy layer is built asynchronously and may never exist (a dead NDBC relay just
 *  warns), and a theater is drawn the moment the user clicks. So fall back through
 *  the other symbol layers, and finally to the first non-raster layer in the style
 *  — anything vector-ish is a symbol/annotation that should stay on top of us,
 *  while everything before it is basemap imagery that should stay under. */
const ABOVE_US = ["buoys", "ov-fleet-live", "ov-ports", "ov-fleet-ports"];

function beforeSymbols(map: MlMap): string | undefined {
  for (const id of ABOVE_US) {
    if (map.getLayer(id)) return id;
  }
  try {
    for (const l of map.getStyle().layers) {
      // Skip our own layers, or the pair would fight: whichever went in first
      // would become the other's anchor and the two would swap depth.
      if (l.id === FRAME_LINE || l.id === GRID_LINE) continue;
      if (l.type !== "raster" && l.type !== "background") return l.id;
    }
  } catch {
    // getStyle() throws while a style swap is in flight; appending is fine then,
    // because refreshTheaterColors/setTheater* run again after the reload.
  }
  return undefined;
}

// ---------- deferred application ----------
//
// Every entry point can be called before the first style has loaded or during a
// swap, when addSource/addLayer throw. Rather than make callers care, remember
// what was asked for and replay it on the next styledata. `once` per map keeps
// the handler from stacking under repeated calls.
//
// The trigger is a caught throw, not an isStyleLoaded() pre-check: that predicate
// also goes false while raster tiles are in flight, so gating on it would refuse
// to draw the frame during an ordinary pan. Failing and retrying is narrower —
// it defers exactly when the style genuinely can't take a layer.
//
// State is module-level, one console = one map (the same assumption overlays.ts
// makes for its port/tropical state).

let wantFrame: Bounds | null = null;
let wantGrid: { b: Bounds; cellDeg: number } | null = null;
const replayArmed = new WeakSet<MlMap>();

function armReplay(map: MlMap): void {
  if (replayArmed.has(map)) return;
  replayArmed.add(map);
  map.once("styledata", () => {
    replayArmed.delete(map);
    // Sources/layers do not survive a style swap, so re-issue from the remembered
    // intent rather than trying to patch whatever is (not) there.
    const f = wantFrame;
    const g = wantGrid;
    setTheaterFrame(map, f);
    setTheaterGrid(map, g === null ? null : g.b, g?.cellDeg);
  });
}

// ---------- geometry ----------

function frameGeoJSON(b: Bounds): GeoJSON.FeatureCollection {
  // A closed ring, not four separate segments: one feature means the corners join
  // cleanly instead of showing four butt caps at the joints.
  const ring: [number, number][] = [
    [b.lonMin, b.latMin],
    [b.lonMax, b.latMin],
    [b.lonMax, b.latMax],
    [b.lonMin, b.latMax],
    [b.lonMin, b.latMin],
  ];
  return {
    type: "FeatureCollection",
    features: [{ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [ring] } }],
  };
}

/** The cell size actually drawn: `cellDeg` widened to a whole multiple of itself
 *  if the frame holds more lines than the cap allows on either axis. */
function drawnCell(b: Bounds, cellDeg: number): number {
  const worst = Math.max(b.latMax - b.latMin, b.lonMax - b.lonMin) / cellDeg;
  const factor = Math.max(1, Math.ceil(worst / MAX_LINES_PER_AXIS));
  return cellDeg * factor;
}

/** Absolute grid lines of spacing `step` inside [lo, hi].
 *
 *  Anchored on zero (the model's own origin) so a line here is a line in the GRIB,
 *  and so two overlapping theaters draw the SAME graticule. The epsilon absorbs
 *  float error at the ends — without it a bound sitting exactly on a cell edge
 *  drops or duplicates that edge depending on the last bit of the division. */
function gridTicks(lo: number, hi: number, step: number): number[] {
  const eps = step * 1e-6;
  const out: number[] = [];
  const first = Math.ceil((lo - eps) / step) * step;
  for (let v = first; v <= hi + eps; v += step) {
    out.push(Number(v.toFixed(6)));
    if (out.length > MAX_LINES_PER_AXIS + 2) break; // belt-and-braces against a pathological step
  }
  return out;
}

function gridGeoJSON(b: Bounds, cellDeg: number): GeoJSON.FeatureCollection {
  const step = drawnCell(b, cellDeg);
  const features: GeoJSON.Feature[] = [];
  // Two-point segments are exact in Web Mercator: a meridian and a parallel are
  // both straight lines there, so no densification is needed. Longitudes are left
  // unwrapped — MapLibre draws past ±180 happily, and splitting at the seam would
  // put a false gap through a theater that legitimately straddles it.
  for (const lat of gridTicks(b.latMin, b.latMax, step)) {
    features.push({
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: [[b.lonMin, lat], [b.lonMax, lat]] },
    });
  }
  for (const lon of gridTicks(b.lonMin, b.lonMax, step)) {
    features.push({
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: [[lon, b.latMin], [lon, b.latMax]] },
    });
  }
  return { type: "FeatureCollection", features };
}

// ---------- source/layer plumbing ----------

/** Create-or-update one geojson line layer. Source and layer are checked
 *  independently because a half-torn-down style can leave one without the other. */
function ensureLine(
  map: MlMap,
  srcId: string,
  layerId: string,
  data: GeoJSON.FeatureCollection,
  paint: Record<string, unknown>,
  /** Insert directly below this layer instead of below the symbol layers — how
   *  the graticule stays under the frame regardless of which was built first. */
  under?: string,
): void {
  const src = map.getSource(srcId);
  if (src !== undefined && "setData" in src) {
    (src as GeoJSONSource).setData(data);
  } else if (src === undefined) {
    map.addSource(srcId, { type: "geojson", data });
  }
  if (map.getLayer(layerId) === undefined) {
    map.addLayer(
      {
        id: layerId,
        type: "line",
        source: srcId,
        layout: { "line-join": "round" },
        paint: paint as Record<string, never>,
      },
      under !== undefined && map.getLayer(under) !== undefined ? under : beforeSymbols(map),
    );
  }
}

function removeLine(map: MlMap, srcId: string, layerId: string): void {
  if (map.getLayer(layerId) !== undefined) map.removeLayer(layerId);
  if (map.getSource(srcId) !== undefined) map.removeSource(srcId);
}

// ---------- public API ----------

/**
 * Draw the confirmed theater as a rectangle outline, or clear it with `null`.
 * Safe to call on every theater change: repeats update the existing source
 * instead of stacking a second one.
 */
export function setTheaterFrame(map: MlMap, b: Bounds | null): void {
  wantFrame = b;
  try {
    if (b === null) {
      removeLine(map, FRAME_SRC, FRAME_LINE);
      return;
    }
    ensureLine(map, FRAME_SRC, FRAME_LINE, frameGeoJSON(b), {
      "line-color": cssVar("--accent"),
      "line-width": FRAME_WIDTH,
      "line-opacity": FRAME_OPACITY,
    });
  } catch {
    // A style swap can start between the readiness check and the call; the replay
    // puts the frame back rather than taking the console down with it.
    armReplay(map);
  }
}

/**
 * Draw the field's true cell boundaries inside the theater — a lat/lon graticule
 * at `cellDeg` (default 0.25, GFS-Wave's grid), anchored on absolute multiples so
 * the lines are the model's edges and not a decorative mesh. Coarsened to whole
 * multiples of `cellDeg` if the frame would otherwise hold too many lines.
 * `null` clears it.
 */
export function setTheaterGrid(map: MlMap, b: Bounds | null, cellDeg: number = DEFAULT_CELL_DEG): void {
  const cell = cellDeg > 0 ? cellDeg : DEFAULT_CELL_DEG;
  wantGrid = b === null ? null : { b, cellDeg: cell };
  try {
    if (b === null) {
      removeLine(map, GRID_SRC, GRID_LINE);
      return;
    }
    ensureLine(
      map,
      GRID_SRC,
      GRID_LINE,
      gridGeoJSON(b, cell),
      {
        "line-color": cssVar("--ink-2"),
        "line-width": GRID_WIDTH,
        "line-opacity": GRID_OPACITY,
      },
      FRAME_LINE,
    );
  } catch {
    armReplay(map);
  }
}

/**
 * The spacing {@link setTheaterGrid} would actually draw for these bounds.
 *
 * Exported so the CALLER can say which edges it is showing. At the basin rung the
 * cap coarsens 0.25 deg to 0.5 — every second cell edge, still a real edge, but a
 * control labelled "model cells" while drawing half of them is the kind of quiet
 * overstatement this whole layer exists to prevent. The label names the number.
 */
export function drawnCellDeg(b: Bounds, cellDeg: number = DEFAULT_CELL_DEG): number {
  return drawnCell(b, cellDeg > 0 ? cellDeg : DEFAULT_CELL_DEG);
}

/**
 * Fit the viewport to the theater. Padding is a fraction of the smaller viewport
 * dimension so the frame lands INSIDE the map with visible water around it — a
 * frame flush to the edge reads as a crop, not as a boundary — and is clamped so
 * it neither disappears on a phone nor eats a wide desktop map.
 */
export function fitTheater(map: MlMap, b: Bounds, opts?: { animate?: boolean }): void {
  const cv = map.getCanvas();
  const short = Math.min(cv.clientWidth || 0, cv.clientHeight || 0);
  const padding = Math.round(Math.max(24, Math.min(80, short * 0.07)));
  map.fitBounds(
    [
      [b.lonMin, b.latMin],
      [b.lonMax, b.latMax],
    ],
    { padding, animate: opts?.animate ?? true },
  );
}

/**
 * Re-read the CSS vars and repaint. The theme switch happens at runtime and these
 * colours were resolved when the layers were built, so they must be re-set — the
 * same contract refreshOverlayColors() has for the overlay layers.
 */
export function refreshTheaterColors(map: MlMap): void {
  if (map.getLayer(FRAME_LINE) !== undefined) {
    map.setPaintProperty(FRAME_LINE, "line-color", cssVar("--accent") as unknown as ExpressionSpecification);
  }
  if (map.getLayer(GRID_LINE) !== undefined) {
    map.setPaintProperty(GRID_LINE, "line-color", cssVar("--ink-2") as unknown as ExpressionSpecification);
  }
}
